import { useCallback, useState } from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'
import trim from 'lodash/trim'
import {
  startPerformanceMeasure,
  stopPerformanceMeasure,
  trackUserEvent,
} from '@vend/utilities'
import { PaymentOptions } from '../PaymentModal/types'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { LRX_39407_CHEQUE_REFERENCE_WRITE } from '../../../../constants/wr-features'
import { shouldUseDirectEconduit } from '../../../../functions/payment-types/econduit'
import { WEB_REGISTER } from '../../../web-register.constant'
import { selectSaleLocalId } from '../../../../state/slices/currentSale/currentSaleSlice'
import { useAppSelector } from '../../../../state/hooks'
import { useTrainingModeManager } from '../../../../ng-compat/hooks'
import { PaymentData } from '../PaymentPanel'
import { shouldCheckSwiperMaintenance } from '../../../../functions/payment-types/integrated'
import { checkSwiperMaintenance } from '../../../payments/payments-resource.service'
import { useHandleCashPayment } from './useHandleCashPayment'
import { useHandleLSPay } from './useHandleLSPay'
import { useHandleIntegrationPayment } from './useHandleIntegrationPayment'
import { useHandleSwiperPayment } from './useHandleSwiperPayment'
import { useHandleBigCommercePayment } from './useHandleBigCommercePayment'
import { useHandleLoyaltyPayment } from './useHandleLoyaltyPayment'
import { useHandleStoreCreditPayment } from './useHandleStoreCreditPayment'
import { useHandleCompletePayment } from './useHandleCompletePayment'
import { useHandleEcomRefundPayment } from './useHandleEcomRefundPayment'

type SelectedPaymentType = {
  paymentOptions: PaymentOptions
}

export const useHandlePaymentType = (
  amountTendered: string,
  paymentProcessing: boolean,
  hasPaymentAmountError: boolean,
  setPaymentProcessing: (value: boolean) => void,
  allowPageReload: () => void
) => {
  const [
    selectedPaymentType,
    setSelectedPaymentType,
  ] = useState<SelectedPaymentType | null>(null)
  const localId = useAppSelector(selectSaleLocalId)
  const trainingModeManager = useTrainingModeManager()

  const clearSelectedPaymentType = useCallback(() => {
    allowPageReload()
    setPaymentProcessing(false)
    setSelectedPaymentType(null)
  }, [allowPageReload, setPaymentProcessing, setSelectedPaymentType])

  const { handleCompletePayment } = useHandleCompletePayment(
    allowPageReload,
    clearSelectedPaymentType
  )

  const { handleCashPayment } = useHandleCashPayment(
    amountTendered,
    handleCompletePayment
  )
  const { handleLSPay } = useHandleLSPay(amountTendered)
  const { handleIntegrationPayment } = useHandleIntegrationPayment(
    amountTendered
  )
  const { handleSwiperPayment } = useHandleSwiperPayment(amountTendered)
  const { handleBigCommercePayment } = useHandleBigCommercePayment(
    amountTendered
  )
  const { handleLoyaltyPayment } = useHandleLoyaltyPayment(
    amountTendered,
    handleCompletePayment
  )
  const { handleStoreCreditPayment } = useHandleStoreCreditPayment(
    amountTendered,
    handleCompletePayment
  )
  const { handleEcomRefundPayment } = useHandleEcomRefundPayment(amountTendered)

  const defaultPaymentHandler = useCallback(
    (
      paymentType: ApiRetailerPaymentType
    ): Promise<PaymentOptions | void> | PaymentOptions | void => {
      const paymentTypeConfig = paymentType.config
      const hasPaymentTypeUrl = paymentTypeConfig && trim(paymentTypeConfig.url)

      if (hasPaymentTypeUrl) {
        if (
          shouldUseDirectEconduit(paymentType) &&
          getFeatureFromCache('direct_econduit')
        ) {
          return handleSwiperPayment(paymentType)
        } else {
          return handleIntegrationPayment(paymentType)
        }
      } else if (
        // Only non-integrated payment types (no config URL) support cashier-entered references.
        // Integrated types own their full payment flow and carry any reference data themselves.
        // payment_reference_enabled is a dynamic backend config key not yet in @vend/types
        (paymentTypeConfig as { payment_reference_enabled?: boolean })
          ?.payment_reference_enabled &&
        getFeatureFromCache(LRX_39407_CHEQUE_REFERENCE_WRITE)
      ) {
        return {
          paymentModalType: 'paymentReference',
          paymentType,
          amount: amountTendered,
        }
      } else {
        handleCompletePayment({
          paymentAmount: amountTendered,
          paymentType,
        })
      }
    },
    [
      amountTendered,
      handleCompletePayment,
      handleIntegrationPayment,
      handleSwiperPayment,
    ]
  )

  const handlePaymentTracking = useCallback(
    (payment: ApiRetailerPaymentType) => {
      // stop the timing when the user selects a payment type
      const measure = stopPerformanceMeasure('select-payment-type-process')
      trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'Payment Type Selected', {
        amount: amountTendered,
        registerSaleId: localId,
        startTime: measure?.startTime || null,
        duration: measure?.duration || null,
        paymentTypeName: payment.name,
        paymentTypeId: payment.type_id,
        retailerPaymentTypeId: payment.id,
        isQuickPay: false,
        quickPayFeatureFlag: getFeatureFromCache(
          'lspay_pay_with_quick_pay_experiment'
        ),
      })

      if (trainingModeManager.trainingModeEnabled) {
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          trainingModeManager.currentTourType?.toString() || '',
          {
            metadata: {
              step: 'Selected Payment Type',
              paymentType: payment.name,
            },
          }
        )
      } else {
        startPerformanceMeasure('payment-tender')
      }
    },
    [
      amountTendered,
      localId,
      trainingModeManager.currentTourType,
      trainingModeManager.trainingModeEnabled,
    ]
  )

  const handlePayment = useCallback(
    async (payment: ApiRetailerPaymentType, paymentData: PaymentData = {}) => {
      if (paymentProcessing) {
        return
      }
      if (hasPaymentAmountError) {
        return
      }

      handlePaymentTracking(payment)

      if (shouldCheckSwiperMaintenance(payment)) {
        const underMaintenance = await checkSwiperMaintenance(payment.type_id)
        if (underMaintenance) {
          return setSelectedPaymentType({
            paymentOptions: {
              paymentType: payment,
              paymentModalType: 'swiperMaintenance',
            },
          })
        }
      }

      setPaymentProcessing(true)

      let paymentOptions: PaymentOptions | void
      switch (payment.type_id) {
        case PAYMENT_TYPES.WORLDPAY:
        case PAYMENT_TYPES.WORLDPAY_TRIPOS:
          paymentOptions = handleSwiperPayment(payment)
          break
        case PAYMENT_TYPES.LSPAY: {
          paymentOptions = handleLSPay(
            payment,
            paymentData.recoveryPaymentOptions
          )
          const isLSPayFlowOption =
            paymentOptions &&
            'outletId' in paymentOptions &&
            paymentOptions.paymentModalType === 'lsPay' &&
            paymentData.lspayFlow

          if (isLSPayFlowOption) {
            paymentOptions.lspayFlow = paymentData.lspayFlow
            if (
              paymentData.lspayFlow === 'pmof' &&
              paymentData.paymentMethodOnFileId
            ) {
              paymentOptions.paymentMethodOnFileId =
                paymentData.paymentMethodOnFileId
            }
          }
          break
        }
        case PAYMENT_TYPES.LIGHTSPEED_ECOM:
          paymentOptions = handleEcomRefundPayment(payment)
          break
        case PAYMENT_TYPES.LOYALTY:
          paymentOptions = handleLoyaltyPayment(payment)
          break
        case PAYMENT_TYPES.GIFT_CARD:
          paymentOptions = {
            paymentModalType: 'giftCard',
            paymentType: payment,
            amount: amountTendered,
          }
          break
        // TODO: Handle gift card
        case PAYMENT_TYPES.STORE_CREDIT:
          paymentOptions = handleStoreCreditPayment(payment)
          break
        case PAYMENT_TYPES.CASH_CONCEALED:
        case PAYMENT_TYPES.CURRENCY:
        case PAYMENT_TYPES.CASH:
          paymentOptions = await handleCashPayment(payment)
          break
        case PAYMENT_TYPES.BIGCOMMERCE_GIFT_CERT:
          paymentOptions = handleBigCommercePayment(payment)
          break
        default:
          paymentOptions = await defaultPaymentHandler(payment)
          break
      }
      if (!paymentOptions) {
        clearSelectedPaymentType()
        return
      }
      setSelectedPaymentType({
        paymentOptions,
      })
    },
    [
      defaultPaymentHandler,
      handleBigCommercePayment,
      handleCashPayment,
      handleLSPay,
      handleEcomRefundPayment,
      handleLoyaltyPayment,
      handleStoreCreditPayment,
      handleSwiperPayment,
      paymentProcessing,
      hasPaymentAmountError,
      handlePaymentTracking,
      setPaymentProcessing,
      amountTendered,
      clearSelectedPaymentType,
    ]
  )

  return {
    selectedPaymentType,
    handlePayment,
    handleCompletePayment,
    clearSelectedPaymentType,
  }
}
